import { useState, useEffect, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { DEFAULT_ACCOUNT_TIMEZONE, resolveScheduleTimezone } from "@/lib/timezones";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect as SharedSearchableSelect } from "@/components/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  ClipboardList, Send, Eye, MapPin, CheckCircle2, Image, Calendar,
  StickyNote, Loader2, Link2, Copy, Users, User, TreePine, Wrench, Sparkles,
  Filter, Plus, Trash2, Edit2, AlertTriangle, Droplets, Bell,
  ExternalLink, Hammer, RefreshCw, Pause, Play, Clock, ChevronsUpDown, Check,
  ChevronDown, ChevronRight, Home, FileText, MessageSquare, Paperclip, X,
  Archive, ArchiveRestore
} from "lucide-react";
import type { ServiceFormTemplate, ServiceFormSubmission, ServiceFormLink, Vendor, Property, Member, ServiceFormSubmissionMessage } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

const FORM_TYPE_LABELS: Record<string, string> = {
  cleaning: "Cleaning",
  landscaping: "Landscaping",
  handyman: "Handyman Inspection",
  swat: "SWAT (Member)",
  pool: "Pool Maintenance",
  other: "Other",
};

const FORM_TYPE_ICONS: Record<string, typeof Sparkles> = {
  cleaning: Sparkles,
  landscaping: TreePine,
  handyman: Wrench,
  swat: Users,
  pool: Droplets,
  other: ClipboardList,
};

const FORM_TYPE_COLORS: Record<string, string> = {
  cleaning: "bg-blue-100 text-blue-700",
  landscaping: "bg-green-100 text-green-700",
  handyman: "bg-orange-100 text-orange-700",
  swat: "bg-purple-100 text-purple-700",
  pool: "bg-cyan-100 text-cyan-700",
  other: "bg-gray-100 text-gray-700",
};

type SearchableOption = { value: string; label: string };

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SearchableOption[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyText?: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((o) => o.value === value);
  const filtered = search.trim()
    ? options.filter((o) => String(o.label ?? "").toLowerCase().includes(search.trim().toLowerCase()))
    : options;
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={selected ? "text-foreground" : "text-muted-foreground"}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder || "Search..."}
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText || "No results found."}</CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => {
                    onChange(opt.value);
                    setSearch("");
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                  <span>{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

type FormSection = {
  id: string;
  title: string;
  checklistItems: Array<{ id: string; label: string; photoRequired: boolean }>;
  photoSlots: Array<{ id: string; label: string; required: boolean }>;
  hasNotes: boolean;
};

function toAuthImageUrl(url: string): string {
  const match = url.match(/\/api\/public\/service-form\/[^/]+\/image\/(\d+)$/);
  if (match) return `/api/app/service-forms/image/${match[1]}`;
  return url;
}

// Task #2102 — thread is keyed by linkId so chat works on both pending
// links and submitted forms; the same thread persists across submit.
function SubmissionMessageThread({ linkId }: { linkId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [messageText, setMessageText] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const messagesQuery = useQuery<ServiceFormSubmissionMessage[]>({
    queryKey: ["/api/app/service-forms/links", linkId, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/app/service-forms/links/${linkId}/messages`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load messages");
      return res.json();
    },
    refetchInterval: 15000,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/service-forms/links/${linkId}/messages`, { body: messageText.trim(), attachments: pendingAttachments });
      return res.json();
    },
    onSuccess: () => {
      setMessageText("");
      setPendingAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/links", linkId, "messages"] });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Failed to send message"), variant: "destructive" }),
  });

  const handleAttachFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const data = reader.result as string;
        const r = await fetch("/api/app/service-forms/upload", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename: file.name, mimeType: file.type, data }),
        });
        if (!r.ok) { toast({ title: "Upload failed", variant: "destructive" }); setAttachmentUploading(false); return; }
        const { url } = await r.json();
        setPendingAttachments(prev => [...prev, url]);
        setAttachmentUploading(false);
      };
      reader.onerror = () => { toast({ title: "Failed to read file", variant: "destructive" }); setAttachmentUploading(false); };
      reader.readAsDataURL(file);
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
      setAttachmentUploading(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const msgs = messagesQuery.data || [];

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" />
          Message Thread
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {messagesQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : msgs.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-2">No messages yet. Send one to start the thread.</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {msgs.map((msg) => {
              const isHost = msg.senderType === "host";
              const msgAttachments: string[] = Array.isArray(msg.attachments) ? (msg.attachments as string[]) : [];
              const senderName = (msg as any).senderName
                || (isHost ? "Host" : (msg.senderType === "vendor_owner" ? "Vendor Owner" : "Team Member"));
              return (
                <div key={msg.id} className={`flex ${isHost ? "justify-end" : "justify-start"}`} data-testid={`msg-submission-${msg.id}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isHost ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-800"}`}>
                    <p className={`text-xs font-medium mb-0.5 ${isHost ? "opacity-90" : "opacity-70"}`} data-testid={`text-host-form-sender-${msg.id}`}>{senderName}</p>
                    <p>{msg.body}</p>
                    {msgAttachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {msgAttachments.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" data-testid={`host-form-attachment-${msg.id}-${i}`}>
                            <img src={url} alt="attachment" className="h-14 w-14 object-cover rounded border" />
                          </a>
                        ))}
                      </div>
                    )}
                    <p className={`text-xs mt-0.5 opacity-60`}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pendingAttachments.map((url, i) => (
              <div key={i} className="relative">
                <img src={url} alt="pending" className="h-12 w-12 object-cover rounded border" />
                <button
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                  onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))}
                  data-testid={`remove-host-form-attachment-${i}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleAttachFile}
          data-testid="input-host-form-attachment"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-9 px-2"
            disabled={attachmentUploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="button-host-form-attach-file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea
            className="flex-1 min-h-0 h-9 resize-none py-2 text-sm"
            placeholder="Type a message…"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && (messageText.trim() || pendingAttachments.length > 0)) {
                e.preventDefault();
                sendMutation.mutate();
              }
            }}
            data-testid="input-submission-message"
          />
          <Button
            size="sm"
            className="h-9 px-3"
            onClick={() => sendMutation.mutate()}
            disabled={(!messageText.trim() && pendingAttachments.length === 0) || sendMutation.isPending || attachmentUploading}
            data-testid="button-send-submission-message"
          >
            {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SubmissionDetail({ submission, template, vendors, onCreateTask }: {
  submission: ServiceFormSubmission;
  template?: ServiceFormTemplate;
  vendors: Vendor[];
  onCreateTask: () => void;
}) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const sections = (template?.sections || []) as FormSection[];
  const checklistResponses = (submission.checklistResponses || {}) as Record<string, boolean>;
  const rawPhotoUrls = (submission.photoUrls || {}) as Record<string, string[]>;
  const photoUrls: Record<string, string[]> = Object.fromEntries(
    Object.entries(rawPhotoUrls).map(([k, v]) => [k, v.map(toAuthImageUrl)])
  );
  const vendor = submission.vendorId ? vendors.find(v => v.id === submission.vendorId) : null;

  const totalItems = sections.reduce((sum, s) => sum + s.checklistItems.length, 0);
  const completedItems = Object.values(checklistResponses).filter(Boolean).length;
  const hasIssues = completedItems < totalItems || submission.locationFlagged;

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      {submission.locationFlagged && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="alert-location-flagged">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Location flagged</p>
            <p className="text-xs text-amber-600">
              Submitted {submission.locationDistanceMiles?.toFixed(1)} miles from the property address.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">Submitted</p>
          <p className="text-sm font-medium">{formatDate(submission.submittedAt)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">Submitter</p>
          <p className="text-sm font-medium">{submission.submitterName || "—"}</p>
        </div>
        {vendor && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">Vendor</p>
            <p className="text-sm font-medium text-brand">{vendor.name}</p>
          </div>
        )}
        {submission.dateOfService && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">Date of Service</p>
            <p className="text-sm font-medium">{submission.dateOfService}</p>
          </div>
        )}
        {submission.nextServiceDate && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">Next Service</p>
            <p className="text-sm font-medium">{submission.nextServiceDate}</p>
          </div>
        )}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">Checklist</p>
          <p className={`text-sm font-medium ${completedItems < totalItems ? "text-amber-600" : "text-green-600"}`}>
            {completedItems}/{totalItems} completed
          </p>
        </div>
        {submission.latitude && submission.longitude && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</p>
            <a
              href={`https://www.google.com/maps?q=${submission.latitude},${submission.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-brand hover:underline"
              data-testid="link-view-map"
            >
              View on map
            </a>
          </div>
        )}
      </div>

      <Button
        className={`w-full ${hasIssues ? "bg-amber-500 hover:bg-amber-600" : "bg-blue-600 hover:bg-blue-700"} text-white`}
        onClick={onCreateTask}
        disabled={readOnly}
        data-testid="button-create-task-from-submission"
      >
        <Hammer className="h-4 w-4 mr-2" />
        Create Task from This Submission
      </Button>

      {sections.map((section) => {
        const sectionPhotos: string[] = [];
        section.photoSlots.forEach((slot) => {
          (photoUrls[slot.id] || []).forEach((url) => sectionPhotos.push(url));
        });
        const sectionItems = section.checklistItems;

        return (
          <Card key={section.id}>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">{section.title}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {sectionItems.length > 0 && (
                <div className="space-y-1.5">
                  {sectionItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      {checklistResponses[item.id] ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${checklistResponses[item.id] ? "text-gray-700" : "text-gray-400"}`}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {sectionPhotos.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Photos ({sectionPhotos.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {sectionPhotos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={url}
                          alt={`Photo ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded border hover:opacity-80 transition-opacity"
                          data-testid={`photo-${section.id}-${idx}`}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {submission.notes && (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><StickyNote className="h-3.5 w-3.5" />Notes</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{submission.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Task-attached submissions (linkId null) chat via task messages instead. */}
      {submission.linkId != null && <SubmissionMessageThread linkId={submission.linkId} />}
    </div>
  );
}

type SectionDraft = {
  id: string;
  title: string;
  checklistItems: Array<{ id: string; label: string; photoRequired: boolean }>;
  photoSlots: Array<{ id: string; label: string; required: boolean }>;
  hasNotes: boolean;
};

function TemplateFormFields({
  name, setName, description, setDescription, formType, setFormType,
  sections, setSections, timeTrackingDefault, setTimeTrackingDefault, isEdit
}: {
  name: string; setName: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  formType: string; setFormType: (v: string) => void;
  sections: SectionDraft[]; setSections: (v: SectionDraft[]) => void;
  timeTrackingDefault: boolean; setTimeTrackingDefault: (v: boolean) => void;
  isEdit?: boolean;
}) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const addSection = () => {
    const id = `section-${Date.now()}`;
    setSections([...sections, { id, title: "", checklistItems: [], photoSlots: [], hasNotes: false }]);
  };

  const removeSection = (idx: number) => {
    setSections(sections.filter((_, i) => i !== idx));
  };

  const updateSection = (idx: number, updates: Partial<SectionDraft>) => {
    setSections(sections.map((s, i) => i === idx ? { ...s, ...updates } : s));
  };

  const addChecklistItem = (sIdx: number) => {
    const updated = [...sections];
    updated[sIdx].checklistItems.push({ id: `item-${Date.now()}`, label: "", photoRequired: false });
    setSections(updated);
  };

  const removeChecklistItem = (sIdx: number, iIdx: number) => {
    const updated = [...sections];
    updated[sIdx].checklistItems = updated[sIdx].checklistItems.filter((_, i) => i !== iIdx);
    setSections(updated);
  };

  const addPhotoSlot = (sIdx: number) => {
    const updated = [...sections];
    updated[sIdx].photoSlots.push({ id: `photo-${Date.now()}`, label: "", required: false });
    setSections(updated);
  };

  const removePhotoSlot = (sIdx: number, pIdx: number) => {
    const updated = [...sections];
    updated[sIdx].photoSlots = updated[sIdx].photoSlots.filter((_, i) => i !== pIdx);
    setSections(updated);
  };

  return (
    <div className="space-y-4 py-2">
      <div>
        <Label>Template Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Deep Clean Checklist" data-testid="input-template-name" />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe when this form should be used" className="resize-none" rows={2} data-testid="input-template-description" />
      </div>
      <div>
        <Label>Form Type</Label>
        <Select value={formType} onValueChange={setFormType}>
          <SelectTrigger data-testid="select-form-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cleaning">Cleaning</SelectItem>
            <SelectItem value="landscaping">Landscaping</SelectItem>
            <SelectItem value="handyman">Handyman Inspection</SelectItem>
            <SelectItem value="swat">SWAT (Member)</SelectItem>
            <SelectItem value="pool">Pool Maintenance</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between rounded-md border px-3 py-2">
        <div>
          <Label className="cursor-pointer" htmlFor="template-time-tracking">Track time by default</Label>
          <p className="text-xs text-gray-500 mt-0.5">
            Form-tasks made from this template show the vendor work timer (e.g. cleaning jobs). Leave off for quick acknowledgment forms.
          </p>
        </div>
        <Switch
          id="template-time-tracking"
          checked={timeTrackingDefault}
          onCheckedChange={setTimeTrackingDefault}
          disabled={readOnly}
          data-testid="switch-template-time-tracking"
        />
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-sm font-semibold">Sections</Label>
          <Button disabled={readOnly} variant="outline" size="sm" onClick={addSection} data-testid="button-add-section">
            <Plus className="h-3 w-3 mr-1" />Section
          </Button>
        </div>
        {sections.map((section, sIdx) => (
          <div key={section.id} className="border rounded-lg p-3 mb-3 space-y-3 bg-gray-50">
            <div className="flex items-center gap-2">
              <Input
                value={section.title}
                onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                placeholder="Section title (e.g. Kitchen)"
                className="flex-1 bg-white"
                data-testid={`input-section-title-${sIdx}`}
              />
              {sections.length > 1 && (
                <Button disabled={readOnly} variant="ghost" size="sm" onClick={() => removeSection(sIdx)} className="text-red-500 hover:text-red-700 px-2">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">Checklist Items</span>
                <Button disabled={readOnly} variant="ghost" size="sm" className="h-6 text-xs" onClick={() => addChecklistItem(sIdx)}>
                  <Plus className="h-3 w-3 mr-1" />Item
                </Button>
              </div>
              {section.checklistItems.map((item, iIdx) => (
                <div key={item.id} className="flex items-center gap-2 mb-1">
                  <Input
                    value={item.label}
                    onChange={(e) => {
                      const updated = [...sections];
                      updated[sIdx].checklistItems[iIdx].label = e.target.value;
                      setSections(updated);
                    }}
                    placeholder="Checklist item label"
                    className="flex-1 h-8 text-sm bg-white"
                    data-testid={`input-checklist-${sIdx}-${iIdx}`}
                  />
                  <Button disabled={readOnly} variant="ghost" size="sm" className="h-6 px-1 text-red-400" onClick={() => removeChecklistItem(sIdx, iIdx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">Photo Upload Slots</span>
                <Button disabled={readOnly} variant="ghost" size="sm" className="h-6 text-xs" onClick={() => addPhotoSlot(sIdx)}>
                  <Plus className="h-3 w-3 mr-1" />Photo
                </Button>
              </div>
              {section.photoSlots.map((slot, pIdx) => (
                <div key={slot.id} className="flex items-center gap-2 mb-1">
                  <Input
                    value={slot.label}
                    onChange={(e) => {
                      const updated = [...sections];
                      updated[sIdx].photoSlots[pIdx].label = e.target.value;
                      setSections(updated);
                    }}
                    placeholder="Photo slot label"
                    className="flex-1 h-8 text-sm bg-white"
                    data-testid={`input-photo-${sIdx}-${pIdx}`}
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={slot.required}
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[sIdx].photoSlots[pIdx].required = e.target.checked;
                        setSections(updated);
                      }}
                      className="rounded"
                    />
                    Required
                  </label>
                  <Button disabled={readOnly} variant="ghost" size="sm" className="h-6 px-1 text-red-400" onClick={() => removePhotoSlot(sIdx, pIdx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={section.hasNotes}
                onChange={(e) => updateSection(sIdx, { hasNotes: e.target.checked })}
                className="rounded"
              />
              Include notes field
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateTemplateDialog() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formType, setFormType] = useState("cleaning");
  const [timeTrackingDefault, setTimeTrackingDefault] = useState(false);
  const [sections, setSections] = useState<SectionDraft[]>([
    { id: "section-1", title: "", checklistItems: [], photoSlots: [], hasNotes: true },
  ]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/service-forms/templates", {
        name, description, formType, sections, timeTrackingDefault,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/templates"] });
      toast({ title: "Template created!" });
      setOpen(false);
      setName("");
      setDescription("");
      setTimeTrackingDefault(false);
      setSections([{ id: "section-1", title: "", checklistItems: [], photoSlots: [], hasNotes: true }]);
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't create this template. Please try again."), variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={readOnly} variant="outline" data-testid="button-create-template" data-tour="service-forms-create">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Service Form Template</DialogTitle>
        </DialogHeader>
        <TemplateFormFields
          name={name} setName={setName}
          description={description} setDescription={setDescription}
          formType={formType} setFormType={setFormType}
          sections={sections} setSections={setSections}
          timeTrackingDefault={timeTrackingDefault} setTimeTrackingDefault={setTimeTrackingDefault}
        />
        <Button
          className="w-full"
          onClick={() => createMutation.mutate()}
          disabled={readOnly || (!name.trim() || sections.some((s) => !s.title.trim()) || createMutation.isPending)}
          data-testid="button-save-template"
        >
          {createMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
          ) : (
            <><Plus className="h-4 w-4 mr-2" />Create Template</>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function EditTemplateDialog({ template }: { template: ServiceFormTemplate }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [formType, setFormType] = useState(template.formType);
  const [timeTrackingDefault, setTimeTrackingDefault] = useState(template.timeTrackingDefault ?? false);
  const [sections, setSections] = useState<SectionDraft[]>(
    (template.sections as SectionDraft[]) || []
  );

  useEffect(() => {
    if (open) {
      setName(template.name);
      setDescription(template.description || "");
      setFormType(template.formType);
      setTimeTrackingDefault(template.timeTrackingDefault ?? false);
      setSections((template.sections as SectionDraft[]) || []);
    }
  }, [open, template]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/app/service-forms/templates/${template.id}`, {
        name, description, formType, sections, timeTrackingDefault,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/templates"] });
      toast({ title: "Template updated!" });
      setOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't update this template. Please try again."), variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2" data-testid={`button-edit-template-${template.id}`}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Template: {template.name}</DialogTitle>
        </DialogHeader>
        <TemplateFormFields
          name={name} setName={setName}
          description={description} setDescription={setDescription}
          formType={formType} setFormType={setFormType}
          sections={sections} setSections={setSections}
          timeTrackingDefault={timeTrackingDefault} setTimeTrackingDefault={setTimeTrackingDefault}
          isEdit
        />
        <Button
          className="w-full"
          onClick={() => updateMutation.mutate()}
          disabled={readOnly || (!name.trim() || sections.some((s) => !s.title.trim()) || updateMutation.isPending)}
          data-testid="button-update-template"
        >
          {updateMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
          ) : (
            <>Save Changes</>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function SendFormDialog({ templates, vendors, properties, members }: {
  templates: ServiceFormTemplate[];
  vendors: Vendor[];
  properties: Property[];
  members: Member[];
}) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [sendSms, setSendSms] = useState(true);
  const [generatedUrl, setGeneratedUrl] = useState("");
  // Forms-on-tasks — vendor-targeted sends create a form-task instead of a
  // tokenized link; the success panel deep-links to the task.
  const [sentTaskId, setSentTaskId] = useState<number | null>(null);

  // Non-archived filter: service forms can be linked to any property that isn't archived
  // (active, underwriting, launch, acquisition, etc.). Active-only would hide valid targets.
  const activeProperties = properties.filter((p) => p.status !== "archive");

  const selectedTemplate = templates.find((t) => t.id === parseInt(templateId));
  const isMemberForm = selectedTemplate?.formType === "swat";

  // Task #1996 — hide moved-out members from the Send Form picker so hosts
  // can't accidentally assign a form to an archived occupant.
  const activeMembers = members.filter((m) => m.status === "active");
  const filteredMembers = propertyId && propertyId !== "none"
    ? activeMembers.filter((m) => (m as { currentPropertyId?: number | null }).currentPropertyId === parseInt(propertyId))
    : activeMembers;

  const isAutoFilled = isMemberForm
    ? (memberId && memberId !== "none" && memberId !== "other")
    : (vendorId && vendorId !== "none" && vendorId !== "other");

  const handleVendorChange = (v: string) => {
    setVendorId(v);
    if (v && v !== "none" && v !== "other") {
      const vendor = vendors.find((vnd) => vnd.id === parseInt(v));
      if (vendor) {
        setRecipientName(vendor.name || "");
        setRecipientPhone(vendor.phone || vendor.contactPhone || "");
      }
    } else if (v === "other" || v === "none") {
      setRecipientName("");
      setRecipientPhone("");
    }
  };

  const handleMemberChange = (v: string) => {
    setMemberId(v);
    if (v && v !== "none" && v !== "other") {
      const member = members.find((m) => m.id === parseInt(v));
      if (member) {
        setRecipientName(member.name || "");
        setRecipientPhone(member.phone || "");
      }
    } else if (v === "other" || v === "none") {
      setRecipientName("");
      setRecipientPhone("");
    }
  };

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/service-forms/send", {
        templateId: parseInt(templateId),
        propertyId: propertyId && propertyId !== "none" ? parseInt(propertyId) : null,
        vendorId: vendorId && vendorId !== "none" && vendorId !== "other" ? parseInt(vendorId) : null,
        memberId: memberId && memberId !== "none" && memberId !== "other" ? parseInt(memberId) : null,
        recipientPhone: recipientPhone || null,
        recipientName: recipientName || null,
        sendSms,
      });
      return res.json();
    },
    onSuccess: (data: { formUrl?: string; task?: { id: number }; smsSent?: boolean; smsError?: string }) => {
      if (data.task) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
        setSentTaskId(data.task.id);
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/links"] });
        setGeneratedUrl(data.formUrl || "");
      }
      if (sendSms && data.smsSent) {
        toast({ title: "Form link sent via SMS" });
      } else if (sendSms && data.smsError) {
        toast({ title: "SMS failed", description: data.smsError, variant: "destructive" });
      }
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't generate this form link. Please try again."), variant: "destructive" });
    },
  });

  const busy = sendMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (busy) return; setOpen(v); if (!v) { setGeneratedUrl(""); setSentTaskId(null); } }}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="button-send-form">
          <Send className="h-4 w-4 mr-2" />
          Send Form
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Send Service Form</DialogTitle>
        </DialogHeader>
        {sentTaskId !== null ? (
          <div className="space-y-4 py-2" data-testid="panel-form-task-created">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">Form sent to the vendor as a task!</p>
              <p className="text-xs text-green-700 mt-1">It appears in their portal task list; the filled form will attach to the task.</p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { window.location.href = `/app/tasks?task=${sentTaskId}`; }}
              data-testid="button-view-form-task"
            >
              View task
            </Button>
            <Button variant="outline" className="w-full" onClick={() => { setSentTaskId(null); setTemplateId(""); }} data-testid="button-send-another">
              Send Another
            </Button>
          </div>
        ) : !generatedUrl ? (
          <div className="space-y-4 py-2">
            <div>
              <Label>Form Template</Label>
              <SearchableSelect
                value={templateId}
                onChange={setTemplateId}
                options={templates.map((t) => ({ value: String(t.id), label: t.name }))}
                placeholder="Select a form template"
                searchPlaceholder="Search templates..."
                emptyText="No templates found."
                testId="select-template"
              />
            </div>
            <div>
              <Label>Property</Label>
              <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setMemberId(""); }}>
                <SelectTrigger data-testid="select-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value="none">No specific property</SelectItem>
                  {activeProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property ${p.id}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isMemberForm ? (
              <div>
                <Label>Vendor</Label>
                <SearchableSelect
                  value={vendorId}
                  onChange={handleVendorChange}
                  options={[
                    { value: "none", label: "No specific vendor" },
                    ...vendors.map((v) => ({ value: String(v.id), label: v.name })),
                    { value: "other", label: "Other" },
                  ]}
                  placeholder="Select a vendor"
                  searchPlaceholder="Search vendors..."
                  emptyText="No vendors found."
                  testId="select-vendor"
                />
              </div>
            ) : (
              <div>
                <Label>Member</Label>
                <Select value={memberId} onValueChange={handleMemberChange}>
                  <SelectTrigger data-testid="select-member">
                    <SelectValue placeholder={filteredMembers.length > 0 ? "Select a member" : "No members for this property"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific member</SelectItem>
                    {filteredMembers.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email || `Member ${m.id}`}</SelectItem>
                    ))}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Recipient Name</Label>
              <Input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Vendor or member name"
                readOnly={!!isAutoFilled}
                className={isAutoFilled ? "bg-gray-50 text-gray-700" : ""}
                data-testid="input-recipient-name"
              />
            </div>
            <div>
              <Label>Recipient Phone</Label>
              <Input
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="+1234567890"
                readOnly={!!isAutoFilled}
                className={isAutoFilled ? "bg-gray-50 text-gray-700" : ""}
                data-testid="input-recipient-phone"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="rounded" />
              Send form link via SMS
            </label>
            <Button
              className="w-full"
              onClick={() => sendMutation.mutate()}
              disabled={readOnly || (!templateId || sendMutation.isPending)}
              data-testid="button-generate-link"
            >
              {sendMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Send className="h-4 w-4 mr-2" />{sendSms ? "Generate & Send" : "Generate & Copy"}</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">Form link generated!</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-2">Form URL</p>
              <div className="flex items-center gap-2">
                <Input value={generatedUrl} readOnly className="text-xs bg-white" data-testid="input-generated-url" />
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(generatedUrl); toast({ title: "Copied!" }); }} data-testid="button-copy-url">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={() => { setGeneratedUrl(""); setTemplateId(""); }} data-testid="button-send-another">
              Send Another
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

type EnrichedLink = ServiceFormLink & { status: "open" | "completed" | "expired"; submissionId: number | null; archivedAt: Date | string | null };

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Monthly" },
];

const DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

// Forms-on-tasks Phase 4 — CreateScheduleDialog / EditScheduleDialog were
// retired with the service-form schedules API; recurring form sends are
// now built as recurring Task Rules (/app/operations/task-rules).

export default function AppOperationsServiceForms() {
  const queryClient = useQueryClient();
  usePageTitle("Service Forms");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [selectedSubmission, setSelectedSubmission] = useState<ServiceFormSubmission | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<ServiceFormTemplate | null>(null);
  const [linkToDelete, setLinkToDelete] = useState<ServiceFormLink | null>(null);
  // Task #2102 — host chat with vendor on a pending (un-submitted) form.
  const [chatLink, setChatLink] = useState<EnrichedLink | null>(null);
  const initialSearchParams = (() => {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  })();
  const [typeFilter, setTypeFilter] = useState(initialSearchParams.get("type") ?? "all");
  const [templateTypeFilter, setTemplateTypeFilter] = useState(initialSearchParams.get("templateType") ?? "all");
  const [propertyFilter, setPropertyFilter] = useState(initialSearchParams.get("property") ?? "all");
  const [vendorFilter, setVendorFilter] = useState(initialSearchParams.get("vendor") ?? "all");
  const [dateFromFilter, setDateFromFilter] = useState(initialSearchParams.get("dateFrom") ?? "");
  const [viewTab, setViewTab] = useTabQueryParam("tab", "submissions", {
    tabs: ["submissions", "links", "recurring"],
  });
  const [linkSearch, setLinkSearch] = useState(initialSearchParams.get("lSearch") ?? "");
  const [linkTemplateFilter, setLinkTemplateFilter] = useState(initialSearchParams.get("lTemplate") ?? "all");
  const [linkPropertyFilter, setLinkPropertyFilter] = useState(initialSearchParams.get("lProperty") ?? "all");
  const [linkRecipientFilter, setLinkRecipientFilter] = useState(initialSearchParams.get("lRecipient") ?? "all");
  const [linkStatusFilter, setLinkStatusFilter] = useState(initialSearchParams.get("lStatus") ?? "all");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const setOrDel = (k: string, v: string, def: string) => {
      if (v && v !== def) params.set(k, v);
      else params.delete(k);
    };
    setOrDel("lSearch", linkSearch, "");
    setOrDel("lTemplate", linkTemplateFilter, "all");
    setOrDel("lProperty", linkPropertyFilter, "all");
    setOrDel("lRecipient", linkRecipientFilter, "all");
    setOrDel("lStatus", linkStatusFilter, "all");
    setOrDel("type", typeFilter, "all");
    setOrDel("templateType", templateTypeFilter, "all");
    setOrDel("property", propertyFilter, "all");
    setOrDel("vendor", vendorFilter, "all");
    setOrDel("dateFrom", dateFromFilter, "");
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (newUrl !== currentUrl) {
      window.history.replaceState(window.history.state, "", newUrl);
    }
  }, [linkSearch, linkTemplateFilter, linkPropertyFilter, linkRecipientFilter, linkStatusFilter, typeFilter, templateTypeFilter, propertyFilter, vendorFilter, dateFromFilter]);
  const [remindingLinkId, setRemindingLinkId] = useState<number | null>(null);
  const [selectedLinkIds, setSelectedLinkIds] = useState<Set<number>>(new Set());
  const [showArchivedLinks, setShowArchivedLinks] = useState(false);
  const [bulkDeleteLinksOpen, setBulkDeleteLinksOpen] = useState(false);

  const templatesQuery = useQuery<(ServiceFormTemplate & { createdByName?: string | null })[]>({
    queryKey: ["/api/app/service-forms/templates"],
  });

  const submissionsQuery = useQuery<ServiceFormSubmission[]>({
    queryKey: ["/api/app/service-forms/submissions"],
  });

  const linksQuery = useQuery<EnrichedLink[]>({
    queryKey: ["/api/app/service-forms/links"],
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const membersQuery = useQuery<Member[]>({
    queryKey: ["/api/app/devices/members"],
  });

  const seedMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/service-forms/templates/seed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/templates"] });
      toast({ title: "Form templates created!" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't seed the form templates. Please try again."), variant: "destructive" });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: async (submissionId: number) => {
      const res = await apiRequest("POST", `/api/app/service-forms/submissions/${submissionId}/create-task`, {});
      return res.json();
    },
    onSuccess: (task: { id: number }) => {
      toast({ title: "Task created!", description: "You can find it in your maintenance tasks." });
      setSelectedSubmission(null);
      navigate(`/app/tasks?task=${task.id}`);
    },
    onError: (e: Error) => {
      toast({ title: "Error creating task", description: parseErrorMessage(e, "Couldn't create this task. Please try again."), variant: "destructive" });
    },
  });

  const remindMutation = useMutation({
    mutationFn: async (linkId: number) => {
      setRemindingLinkId(linkId);
      const res = await apiRequest("POST", `/api/app/service-forms/links/${linkId}/remind`);
      return res.json();
    },
    onSuccess: (data: { reminded: boolean; error?: string }) => {
      setRemindingLinkId(null);
      if (data.reminded) {
        toast({ title: "Reminder sent!" });
      } else {
        toast({ title: "Reminder failed", description: data.error, variant: "destructive" });
      }
    },
    onError: (e: Error) => {
      setRemindingLinkId(null);
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't send the reminder. Please try again."), variant: "destructive" });
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      const res = await apiRequest("DELETE", `/api/app/service-forms/links/${linkId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/links"] });
      toast({ title: "Form link deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't delete this link. Please try again."), variant: "destructive" });
    },
  });

  const bulkDeleteLinksMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await apiRequest("POST", "/api/app/service-forms/links/bulk-delete", { ids });
      return res.json() as Promise<{ success: boolean; deletedCount: number; skipped: { id: number; reason: string }[] }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/links"] });
      setSelectedLinkIds(new Set());
      const skippedSubmissions = data.skipped.filter((s) => s.reason === "has_submission").length;
      const description = skippedSubmissions > 0
        ? `${data.deletedCount} deleted. ${skippedSubmissions} skipped because they have completed submissions — archive those instead.`
        : `${data.deletedCount} deleted.`;
      toast({ title: "Form links deleted", description });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't delete the selected links. Please try again."), variant: "destructive" });
    },
  });

  const bulkArchiveLinksMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ ids, archived }: { ids: number[]; archived: boolean }) => {
      const res = await apiRequest("POST", "/api/app/service-forms/links/bulk-archive", { ids, archived });
      return res.json() as Promise<{ success: boolean; updatedCount: number; archived: boolean; skipped: { id: number; reason: string }[] }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/links"] });
      setSelectedLinkIds(new Set());
      const skippedCount = data.skipped.length;
      const skippedSuffix = skippedCount > 0
        ? ` ${skippedCount} skipped (${Array.from(new Set(data.skipped.map((s) => s.reason))).join(", ")}).`
        : "";
      toast({
        title: data.archived ? "Form links archived" : "Form links unarchived",
        description: `${data.updatedCount} ${data.archived ? "archived" : "unarchived"}.${skippedSuffix}`,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't update the selected links. Please try again."), variant: "destructive" });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const res = await apiRequest("DELETE", `/api/app/service-forms/templates/${templateId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/service-forms/templates"] });
      toast({ title: "Template deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Error deleting template", description: parseErrorMessage(e, "Couldn't delete this template. It may still be in use."), variant: "destructive" });
    },
  });

  const templates = templatesQuery.data || [];
  const submissions = submissionsQuery.data || [];
  const links = linksQuery.data || [];

  // Task #2106 — auto-open the chat dialog when arriving via the in-app
  // notification deep-link `/operations?formLink=<id>` (mirrors the
  // `?submission=` pattern). Switches to the Links tab so the open dialog
  // makes contextual sense, then strips the param so refresh doesn't re-open.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!linksQuery.data) return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("formLink");
    if (!raw) return;
    const linkId = parseInt(raw, 10);
    if (Number.isNaN(linkId)) {
      params.delete("formLink");
      const qs = params.toString();
      window.history.replaceState(window.history.state, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
      return;
    }
    const match = linksQuery.data.find((l) => l.id === linkId);
    if (match) {
      setChatLink(match);
      setViewTab("links");
    }
    params.delete("formLink");
    const qs = params.toString();
    window.history.replaceState(window.history.state, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
  }, [linksQuery.data, setViewTab]);
  const vendors = vendorsQuery.data || [];
  const properties = sortProperties(propertiesQuery.data || []);
  // Task #1996 — every downstream picker on this page (Send Form,
  // Create/Edit Schedule) only ever wants active members. Filter at the
  // source so any future consumer inherits the same guarantee.
  const members = (membersQuery.data || []).filter((m) => m.status === "active");

  const templateMap = new Map(templates.map((t) => [t.id, t]));
  const vendorMap = new Map(vendors.map((v) => [v.id, v]));
  const propertyMap = new Map(properties.map((p) => [p.id, p]));
  const memberMap = new Map(members.map((m) => [m.id, m]));


  const filteredSubmissions = submissions.filter((s) => {
    if (typeFilter !== "all" && templateMap.get(s.templateId)?.formType !== typeFilter) return false;
    if (propertyFilter !== "all" && String(s.propertyId) !== propertyFilter) return false;
    if (vendorFilter !== "all") {
      if (vendorFilter.startsWith("v:") && String(s.vendorId) !== vendorFilter.slice(2)) return false;
      if (vendorFilter.startsWith("m:") && String(s.memberId) !== vendorFilter.slice(2)) return false;
    }
    if (dateFromFilter) {
      const submittedDate = s.submittedAt ? new Date(s.submittedAt).toISOString().slice(0, 10) : "";
      if (submittedDate < dateFromFilter) return false;
    }
    return true;
  });

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="page-service-forms">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-service-forms-title" data-tour="service-forms-heading">Forms</h1>
            <p className="text-sm text-gray-500 mt-1">
              Send inspection forms to vendors and members, review submissions with photos and checklists.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {templates.length === 0 && !templatesQuery.isLoading && (
              <Button
                variant="outline"
                onClick={() => seedMutation.mutate()}
                disabled={readOnly || (seedMutation.isPending)}
                data-testid="button-seed-templates"
              >
                {seedMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-2" />}
                {seedMutation.isPending ? "Creating..." : "Seed Pre-built Templates"}
              </Button>
            )}
            <CreateTemplateDialog />
            <SendFormDialog
              templates={templates}
              vendors={vendors}
              properties={properties}
              members={members}
            />
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="service-forms" />
              <GuideButton pageKey="service-forms" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="service-forms" />
        </div>

        <div className="flex items-center gap-3 mb-3">
          <span className="text-sm font-medium text-gray-700">Templates</span>
          <Select value={templateTypeFilter} onValueChange={setTemplateTypeFilter}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="select-template-type-filter">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="cleaning">Cleaning</SelectItem>
              <SelectItem value="landscaping">Landscaping</SelectItem>
              <SelectItem value="handyman">Handyman</SelectItem>
              <SelectItem value="swat">SWAT</SelectItem>
              <SelectItem value="pool">Pool</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          {templateTypeFilter !== "all" && (
            <button className="text-xs text-blue-600 hover:underline" onClick={() => setTemplateTypeFilter("all")} data-testid="button-clear-template-filter">Clear</button>
          )}
        </div>
        <div className="grid grid-cols-5 gap-4 mb-6">
          {templates.filter((t) => templateTypeFilter === "all" || t.formType === templateTypeFilter).map((template) => {
            const Icon = FORM_TYPE_ICONS[template.formType] || ClipboardList;
            const count = submissions.filter((s) => s.templateId === template.id).length;
            return (
              <Card key={template.id} data-testid={`card-template-${template.id}`} className="relative group">
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  <EditTemplateDialog template={template} />
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`button-delete-template-${template.id}`}
                      title="Delete template"
                      onClick={() => setTemplateToDelete(template)}
                      disabled={deleteTemplateMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium mb-2 ${FORM_TYPE_COLORS[template.formType] || "bg-gray-100 text-gray-700"}`}>
                    <Icon className="h-3 w-3" />
                    {FORM_TYPE_LABELS[template.formType] || template.formType}
                  </div>
                  <p className="text-sm font-medium text-gray-900">{template.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{count} submission{count !== 1 ? "s" : ""}</p>
                  {template.createdByName && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1" data-testid={`text-created-by-${template.id}`}>
                      <User className="h-3 w-3" />Created by {template.createdByName}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {templatesQuery.isLoading && [1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
          {!templatesQuery.isLoading && templates.length === 0 && (
            <div className="col-span-5 text-center py-8 border-2 border-dashed rounded-lg">
              <ClipboardList className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No templates yet. Click "Create Template" to get started.</p>
            </div>
          )}
        </div>

        <Tabs value={viewTab} onValueChange={setViewTab}>
          <TabsList>
            <TabsTrigger value="submissions" data-testid="tab-submissions">
              Submissions ({submissions.length})
            </TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links">
              {(() => {
                const activeCount = links.filter((l) => !l.archivedAt).length;
                const archivedCount = links.length - activeCount;
                return `Sent Links (${activeCount}${archivedCount > 0 ? ` + ${archivedCount} archived` : ""})`;
              })()}
            </TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-recurring">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Recurring
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]" data-testid="select-type-filter">
                  <SelectValue placeholder="All form types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All form types</SelectItem>
                  <SelectItem value="cleaning">Cleaning</SelectItem>
                  <SelectItem value="landscaping">Landscaping</SelectItem>
                  <SelectItem value="handyman">Handyman Inspection</SelectItem>
                  <SelectItem value="swat">SWAT (Member)</SelectItem>
                  <SelectItem value="pool">Pool Maintenance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <SharedSearchableSelect
                value={propertyFilter}
                onValueChange={setPropertyFilter}
                placeholder="All properties"
                searchPlaceholder="Search properties..."
                emptyText="No properties match."
                testId="select-property-filter"
                className="w-[160px]"
                options={[
                  { value: "all", label: "All properties" },
                  ...properties.map((p) => ({
                    value: String(p.id),
                    label: p.name || p.address || `Property ${p.id}`,
                    keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                  })),
                ]}
              />
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger className="w-[160px]" data-testid="select-vendor-filter">
                  <SelectValue placeholder="All vendors/members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors/members</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={`v-${v.id}`} value={`v:${v.id}`}>{v.name}</SelectItem>
                  ))}
                  {members.map((m) => (
                    <SelectItem key={`m-${m.id}`} value={`m:${m.id}`}>{m.name || m.email || `Member ${m.id}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">From</span>
                <input
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm text-gray-700 h-9 bg-white"
                  data-testid="input-date-filter"
                />
              </div>
              {(typeFilter !== "all" || propertyFilter !== "all" || vendorFilter !== "all" || dateFromFilter) && (
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => { setTypeFilter("all"); setPropertyFilter("all"); setVendorFilter("all"); setDateFromFilter(""); }}
                  data-testid="button-clear-filters"
                >
                  Clear filters
                </button>
              )}
            </div>

            {submissionsQuery.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed rounded-lg" data-testid="text-no-submissions">
                <ClipboardList className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-600">No submissions yet</p>
                <p className="text-xs text-gray-400 mt-1">Send a form link to your vendors or members to get started.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto" data-tour="service-forms-submissions">
                <table className="w-full min-w-[720px]" data-testid="table-submissions">
                  <thead>
                    <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Form Type</th>
                      <th className="px-4 py-3">Vendor</th>
                      <th className="px-4 py-3">Submitter</th>
                      <th className="px-4 py-3">Property</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Photos</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((submission) => {
                      const template = templateMap.get(submission.templateId);
                      const property = submission.propertyId ? propertyMap.get(submission.propertyId) : null;
                      const vendor = submission.vendorId ? vendorMap.get(submission.vendorId) : null;
                      const photoUrls = (submission.photoUrls || {}) as Record<string, string[]>;
                      const photoCount = Object.values(photoUrls).reduce((sum, arr) => sum + arr.length, 0);
                      const Icon = template ? FORM_TYPE_ICONS[template.formType] || ClipboardList : ClipboardList;
                      return (
                        <tr
                          key={submission.id}
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedSubmission(submission)}
                          data-testid={`row-submission-${submission.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${template ? FORM_TYPE_COLORS[template.formType] : "bg-gray-100 text-gray-600"}`}>
                              <Icon className="h-3 w-3" />
                              {template ? FORM_TYPE_LABELS[template.formType] : "Unknown"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {vendor ? (
                              <button
                                className="text-sm text-brand hover:underline font-medium"
                                onClick={(e) => { e.stopPropagation(); navigate(`/app/operations/vendors`); }}
                                data-testid={`link-vendor-${vendor.id}`}
                              >
                                {vendor.name}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{submission.submitterName || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{property?.name || property?.address || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(submission.submittedAt)}</td>
                          <td className="px-4 py-3">
                            {photoCount > 0 ? (
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <Image className="h-3.5 w-3.5" />
                                {photoCount}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {submission.locationFlagged ? (
                              <div className="flex items-center gap-1" title={`${submission.locationDistanceMiles?.toFixed(1)} mi from property`}>
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                              </div>
                            ) : submission.latitude && submission.longitude ? (
                              <MapPin className="h-4 w-4 text-green-500" />
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setSelectedSubmission(submission); }}
                              data-testid={`button-view-submission-${submission.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="links" className="mt-4">
            {linksQuery.isLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : links.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed rounded-lg" data-testid="text-no-links">
                <Link2 className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-600">No form links sent yet</p>
                <p className="text-xs text-gray-400 mt-1">Use "Send Form" to create and send form links.</p>
              </div>
            ) : (
              (() => {
                const archivedCount = links.filter((l) => !!l.archivedAt).length;
                const archivedScopedLinks = links.filter((l) => showArchivedLinks ? !!l.archivedAt : !l.archivedAt);
                const linkSearchQ = linkSearch.trim().toLowerCase();
                const visibleLinks = archivedScopedLinks.filter((l) => {
                  if (linkTemplateFilter !== "all" && String(l.templateId) !== linkTemplateFilter) return false;
                  if (linkPropertyFilter !== "all") {
                    if (linkPropertyFilter === "none") {
                      if (l.propertyId) return false;
                    } else if (String(l.propertyId) !== linkPropertyFilter) return false;
                  }
                  if (linkRecipientFilter !== "all") {
                    if (linkRecipientFilter === "vendor" && !l.vendorId) return false;
                    else if (linkRecipientFilter === "member" && !l.memberId) return false;
                    else if (linkRecipientFilter === "custom" && (l.vendorId || l.memberId)) return false;
                    else if (linkRecipientFilter.startsWith("v:") && String(l.vendorId) !== linkRecipientFilter.slice(2)) return false;
                    else if (linkRecipientFilter.startsWith("m:") && String(l.memberId) !== linkRecipientFilter.slice(2)) return false;
                  }
                  if (linkStatusFilter !== "all" && l.status !== linkStatusFilter) return false;
                  if (linkSearchQ) {
                    const template = templateMap.get(l.templateId);
                    const property = l.propertyId ? propertyMap.get(l.propertyId) : null;
                    const vendor = l.vendorId ? vendorMap.get(l.vendorId) : null;
                    const member = l.memberId ? memberMap.get(l.memberId) : null;
                    const haystack = [
                      l.recipientName,
                      l.recipientPhone,
                      template?.name,
                      property?.name,
                      property?.address,
                      vendor?.name,
                      member?.name,
                      member?.email,
                    ].filter(Boolean).join(" ").toLowerCase();
                    if (!haystack.includes(linkSearchQ)) return false;
                  }
                  return true;
                });
                const filtersActive = linkSearch !== "" || linkTemplateFilter !== "all" || linkPropertyFilter !== "all" || linkRecipientFilter !== "all" || linkStatusFilter !== "all";
                return (
                <div className="space-y-3" data-tour="service-forms-list">
                <div className="flex flex-wrap items-center gap-3">
                  <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <Input
                    type="search"
                    placeholder="Search links…"
                    value={linkSearch}
                    onChange={(e) => setLinkSearch(e.target.value)}
                    className="w-[200px] h-9"
                    data-testid="input-link-search"
                  />
                  <Select value={linkTemplateFilter} onValueChange={setLinkTemplateFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="select-link-template-filter">
                      <SelectValue placeholder="All forms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All forms</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={`lt-${t.id}`} value={String(t.id)}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <SharedSearchableSelect
                    value={linkPropertyFilter}
                    onValueChange={setLinkPropertyFilter}
                    placeholder="All properties"
                    searchPlaceholder="Search properties..."
                    emptyText="No properties match."
                    testId="select-link-property-filter"
                    className="w-[180px]"
                    options={[
                      { value: "all", label: "All properties" },
                      { value: "none", label: "No property" },
                      ...properties.map((p) => ({
                        value: String(p.id),
                        label: p.name || p.address || `Property ${p.id}`,
                        keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                      })),
                    ]}
                  />
                  <Select value={linkRecipientFilter} onValueChange={setLinkRecipientFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="select-link-recipient-filter">
                      <SelectValue placeholder="All recipients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All recipients</SelectItem>
                      <SelectItem value="vendor">Any vendor</SelectItem>
                      <SelectItem value="member">Any member</SelectItem>
                      <SelectItem value="custom">Custom (no vendor/member)</SelectItem>
                      {vendors.map((v) => (
                        <SelectItem key={`lv-${v.id}`} value={`v:${v.id}`}>{v.name}</SelectItem>
                      ))}
                      {members.map((m) => (
                        <SelectItem key={`lm-${m.id}`} value={`m:${m.id}`}>{m.name || m.email || `Member ${m.id}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={linkStatusFilter} onValueChange={setLinkStatusFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="select-link-status-filter">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                  {filtersActive && (
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => { setLinkSearch(""); setLinkTemplateFilter("all"); setLinkPropertyFilter("all"); setLinkRecipientFilter("all"); setLinkStatusFilter("all"); }}
                      data-testid="button-clear-link-filters"
                    >
                      Clear filters
                    </button>
                  )}
                  <span className="text-xs text-gray-500 ml-auto" data-testid="text-link-count">
                    {visibleLinks.length} of {archivedScopedLinks.length}
                  </span>
                </div>
                {!readOnly && (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 border rounded-md" data-testid="bulk-actions-links">
                    <div className="text-sm text-gray-600" data-testid="text-selected-count">
                      {selectedLinkIds.size > 0
                        ? `${selectedLinkIds.size} selected`
                        : (showArchivedLinks
                            ? `Viewing ${archivedCount} archived link${archivedCount === 1 ? "" : "s"}`
                            : "Select rows to act on them in bulk")}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedLinkIds.size > 0 && !showArchivedLinks && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const ids = Array.from(selectedLinkIds);
                              bulkArchiveLinksMutation.mutate({ ids, archived: true });
                            }}
                            disabled={bulkArchiveLinksMutation.isPending}
                            data-testid="button-bulk-archive-links"
                          >
                            {bulkArchiveLinksMutation.isPending ? (
                              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Archiving…</>
                            ) : (
                              <><Archive className="h-4 w-4 mr-1" /> Archive selected</>
                            )}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setBulkDeleteLinksOpen(true)}
                            disabled={bulkDeleteLinksMutation.isPending}
                            data-testid="button-bulk-delete-links"
                          >
                            {bulkDeleteLinksMutation.isPending ? (
                              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting…</>
                            ) : (
                              <><Trash2 className="h-4 w-4 mr-1" /> Delete selected</>
                            )}
                          </Button>
                        </>
                      )}
                      {selectedLinkIds.size > 0 && showArchivedLinks && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const ids = Array.from(selectedLinkIds);
                            bulkArchiveLinksMutation.mutate({ ids, archived: false });
                          }}
                          disabled={bulkArchiveLinksMutation.isPending}
                          data-testid="button-bulk-unarchive-links"
                        >
                          {bulkArchiveLinksMutation.isPending ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Unarchiving…</>
                          ) : (
                            <><ArchiveRestore className="h-4 w-4 mr-1" /> Unarchive selected</>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowArchivedLinks((v) => !v);
                          setSelectedLinkIds(new Set());
                        }}
                        data-testid="button-toggle-archived-links"
                      >
                        {showArchivedLinks
                          ? "View active"
                          : `View archived${archivedCount > 0 ? ` (${archivedCount})` : ""}`}
                      </Button>
                    </div>
                  </div>
                )}
                {visibleLinks.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg" data-testid="text-no-visible-links">
                    <Link2 className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-600">
                      {showArchivedLinks ? "No archived form links" : "No active form links"}
                    </p>
                    {showArchivedLinks ? (
                      <p className="text-xs text-gray-400 mt-1">Archived links you create will appear here.</p>
                    ) : archivedCount > 0 ? (
                      <p className="text-xs text-gray-400 mt-1">Use "View archived" above to see {archivedCount} archived link{archivedCount === 1 ? "" : "s"}.</p>
                    ) : null}
                  </div>
                ) : (
                <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[720px]" data-testid="table-links">
                  <thead>
                    <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                      {!readOnly && (
                        <th className="px-4 py-3 w-10">
                          {(() => {
                            const selectableIds = visibleLinks.map((l) => l.id);
                            const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedLinkIds.has(id));
                            const someSelected = selectableIds.some((id) => selectedLinkIds.has(id));
                            return (
                              <Checkbox
                                checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedLinkIds(new Set(selectableIds));
                                  } else {
                                    setSelectedLinkIds(new Set());
                                  }
                                }}
                                disabled={selectableIds.length === 0}
                                data-testid="checkbox-select-all-links"
                                aria-label="Select all visible links"
                              />
                            );
                          })()}
                        </th>
                      )}
                      <th className="px-4 py-3">Form</th>
                      <th className="px-4 py-3">Property</th>
                      <th className="px-4 py-3">Recipient</th>
                      <th className="px-4 py-3">Sent</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLinks.map((link) => {
                      const template = templateMap.get(link.templateId);
                      const property = link.propertyId ? propertyMap.get(link.propertyId) : null;
                      const formUrl = `${window.location.origin}/service-form/${link.token}`;
                      const isSelected = selectedLinkIds.has(link.id);
                      return (
                        <tr key={link.id} className="border-b" data-testid={`row-link-${link.id}`}>
                          {!readOnly && (
                            <td className="px-4 py-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => {
                                  setSelectedLinkIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) next.add(link.id); else next.delete(link.id);
                                    return next;
                                  });
                                }}
                                data-testid={`checkbox-link-${link.id}`}
                                aria-label="Select link"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {template?.name || "Unknown Form"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {property?.name || property?.address || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {link.recipientName || link.recipientPhone || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(link.sentAt)}</td>
                          <td className="px-4 py-3">
                            {link.status === "completed" ? (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid={`badge-status-${link.id}`}>
                                <CheckCircle2 className="h-3 w-3 mr-1" />Completed
                              </Badge>
                            ) : link.status === "expired" ? (
                              <Badge variant="secondary" className="text-xs" data-testid={`badge-status-${link.id}`}>Expired</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100" data-testid={`badge-status-${link.id}`}>
                                Open
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setChatLink(link)}
                                data-testid={`button-chat-link-${link.id}`}
                                title="Chat with vendor"
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { navigator.clipboard.writeText(formUrl); toast({ title: "Link copied!" }); }}
                                data-testid={`button-copy-link-${link.id}`}
                                title="Copy link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              {link.status === "open" && link.recipientPhone && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => remindMutation.mutate(link.id)}
                                  disabled={readOnly || (remindingLinkId === link.id)}
                                  data-testid={`button-remind-${link.id}`}
                                  title="Send reminder"
                                >
                                  {remindingLinkId === link.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Bell className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {!readOnly && (
                                link.archivedAt ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => bulkArchiveLinksMutation.mutate({ ids: [link.id], archived: false })}
                                    disabled={bulkArchiveLinksMutation.isPending}
                                    data-testid={`button-unarchive-link-${link.id}`}
                                    title="Unarchive link"
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => bulkArchiveLinksMutation.mutate({ ids: [link.id], archived: true })}
                                    disabled={bulkArchiveLinksMutation.isPending}
                                    data-testid={`button-archive-link-${link.id}`}
                                    title="Archive link"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                )
                              )}
                              {!readOnly && link.status !== "completed" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setLinkToDelete(link)}
                                  disabled={deleteLinkMutation.isPending}
                                  data-testid={`button-delete-link-${link.id}`}
                                  title="Delete link"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                )}
                </div>
                );
              })()
            )}
          </TabsContent>

          <TabsContent value="recurring" className="mt-4">
            {/* Forms-on-tasks Phase 4 — recurring form sends moved to the
                unified Task Rules engine; this tab is a signpost. */}
            <div className="text-center py-16 border-2 border-dashed rounded-lg" data-testid="panel-recurring-moved">
              <RefreshCw className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">Recurring forms are now Task Rules</p>
              <p className="text-xs text-gray-500 mb-4 max-w-md mx-auto">
                Build a recurring rule with a form template and it will spawn a form-task for your vendor
                (or send a member a form link) on your schedule.
              </p>
              <Button asChild data-testid="button-open-task-rules">
                <Link href="/app/operations/task-rules">Open Task Rules</Link>
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!chatLink} onOpenChange={(v) => !v && setChatLink(null)}>
        <DialogContent className="max-w-xl" data-testid="dialog-link-chat">
          <DialogHeader>
            <DialogTitle>
              {chatLink ? `Chat – ${templateMap.get(chatLink.templateId)?.name || "Form"}${chatLink.recipientName ? ` (${chatLink.recipientName})` : ""}` : "Chat"}
            </DialogTitle>
          </DialogHeader>
          {chatLink && <SubmissionMessageThread linkId={chatLink.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedSubmission} onOpenChange={(v) => !v && setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {selectedSubmission && templateMap.get(selectedSubmission.templateId)?.name || "Submission"}
            </DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              <SubmissionDetail
                submission={selectedSubmission}
                template={templateMap.get(selectedSubmission.templateId)}
                vendors={vendors}
                onCreateTask={() => createTaskMutation.mutate(selectedSubmission.id)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!templateToDelete} onOpenChange={(v) => !v && setTemplateToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-template-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              {templateToDelete
                ? `Delete template "${templateToDelete.name}"? This cannot be undone.`
                : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-template">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-template"
              disabled={deleteTemplateMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!templateToDelete) return;
                const id = templateToDelete.id;
                deleteTemplateMutation.mutate(id, {
                  onSettled: () => setTemplateToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!linkToDelete} onOpenChange={(v) => !v && setLinkToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-link-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete form link?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete this form link? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-link">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-link"
              disabled={deleteLinkMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!linkToDelete) return;
                const id = linkToDelete.id;
                deleteLinkMutation.mutate(id, {
                  onSettled: () => setLinkToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteLinkMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={bulkDeleteLinksOpen} onOpenChange={setBulkDeleteLinksOpen}>
        <AlertDialogContent data-testid="dialog-bulk-delete-links-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedLinkIds.size} selected form link{selectedLinkIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-bulk-delete-links">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-bulk-delete-links"
              disabled={bulkDeleteLinksMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                const ids = Array.from(selectedLinkIds);
                if (ids.length === 0) {
                  setBulkDeleteLinksOpen(false);
                  return;
                }
                bulkDeleteLinksMutation.mutate(ids, {
                  onSettled: () => setBulkDeleteLinksOpen(false),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {bulkDeleteLinksMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
